import React from "react";
import { Box, Sheet, Table, Chip, CircularProgress, Option, Card } from "@mui/joy";
import { DeleteForeverIcon, CheckRoundedIcon, CloseRoundedIcon } from "styles/StyledMUI";
import DarkTypography from "components/common/DarkTypography";
import DarkCard from "components/common/DarkCard";
import DarkSelect from "components/common/DarkSelect";
import YearMonthPeriodSelector from "components/common/YearMonthPeriodSelector";
import { useAuth, useExcursion } from "contexts/api";
import { usePermissions, Permission } from "contexts/app/permissions";
import { useConfirmationModal } from "contexts/app/confirmation";
import useAppSettings from "contexts/app/appSettings";
import type { InstructorStat } from "api/stats/Stats.typed";
import type { Excursion, ExcursionBooking, BookingStatus } from "api/excursions/Excursion.typed";
import {
  hrSheetSx,
  hrTableSx,
  textTokens,
  hrMobileRequestCardSx,
  excursionBookingCompactCtaSx,
  excursionBookingMobileDeleteCtaSx,
} from "styles/globalStyles";
import DarkCheckbox from "components/common/DarkCheckbox";
import IconButtonCTA from "components/common/IconButtonCTA";
import InstructorEncadrantFilter, {
  INSTRUCTOR_FILTER_ALL,
} from "components/common/InstructorEncadrantFilter";
import ExcursionPlanningGroupsDrawer from "components/excursions/planning/display/ExcursionPlanningGroupsDrawer";
import { useExcursionLabels } from "components/excursions/planning/hooks/useExcursionLabels";
import { useExcursionBookingsForMonth } from "./hooks/useExcursionBookingsForMonth";

function instructorOptionFromExcursionBooking(
  b: Pick<ExcursionBooking, "user_name" | "user_id"> & { user_id: number },
  getUserName: (id: number) => string | undefined,
): Pick<InstructorStat, "id" | "first_name" | "last_name"> {
  const full = (b.user_name && b.user_name.trim()) || getUserName(b.user_id) || "";
  if (!full.trim()) {
    return { id: b.user_id, first_name: "", last_name: "" };
  }
  const idx = full.indexOf(" ");
  if (idx === -1) {
    return { id: b.user_id, first_name: full.trim(), last_name: "" };
  }
  return {
    id: b.user_id,
    first_name: full.slice(0, idx).trim(),
    last_name: full.slice(idx + 1).trim(),
  };
}

function sortBookingInstructorOptions(
  opts: Pick<InstructorStat, "id" | "first_name" | "last_name">[],
): Pick<InstructorStat, "id" | "first_name" | "last_name">[] {
  return [...opts].sort((a, b) => {
    const ln = a.last_name.localeCompare(b.last_name, "fr");
    if (ln !== 0) return ln;
    return a.first_name.localeCompare(b.first_name, "fr");
  });
}

const STATUS_CONFIG: Record<
  BookingStatus,
  { label: string; color: "success" | "warning" | "danger" | "neutral" }
> = {
  validated: { label: "Validée", color: "success" },
  pending: { label: "En attente", color: "warning" },
  rejected: { label: "Refusée", color: "danger" },
  cancelled: { label: "Annulée", color: "neutral" },
};

const BOOKING_STATUS_FILTER_ALL = "all" as const;
type BookingStatusFilterValue = typeof BOOKING_STATUS_FILTER_ALL | BookingStatus;

const BOOKING_STATUS_OPTIONS: BookingStatus[] = ["pending", "validated", "rejected", "cancelled"];

function formatDate(raw?: string): string {
  if (!raw) return "—";
  const d = new Date(raw);
  if (isNaN(d.getTime())) return raw;
  return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" });
}

function formatTime(start?: string | null, end?: string | null): string {
  const parts = [start, end].filter(Boolean);
  return parts.length ? parts.join(" – ") : "";
}

/** HR table rows only: full FKs, never minimized cross-tenant slots. */
type ManagedExcursionBooking = ExcursionBooking & {
  status: BookingStatus;
  user_id: number;
  excursion_id: number;
  company_id: number;
};

function isExcursionBookingForManagementUI(b: ExcursionBooking): b is ManagedExcursionBooking {
  return (
    b.planning_anonymized !== true &&
    b.status != null &&
    typeof b.user_id === "number" &&
    typeof b.excursion_id === "number" &&
    typeof b.company_id === "number"
  );
}

function formatTenant(b: ManagedExcursionBooking): string {
  const name = b.company_name?.trim();
  if (name) return name;
  if (b.company_id === 0) return "—";

  return `#${b.company_id}`;
}

type UserBookings = {
  userId: number;
  userName: string;
  bookings: ManagedExcursionBooking[];
};

function groupByUser(
  bookings: ManagedExcursionBooking[],
  getUserName: (id: number) => string | undefined,
): UserBookings[] {
  const map = new Map<number, UserBookings>();

  for (const b of bookings) {
    let entry = map.get(b.user_id);
    if (!entry) {
      entry = {
        userId: b.user_id,
        userName: getUserName(b.user_id) || `#${b.user_id}`,
        bookings: [],
      };
      map.set(b.user_id, entry);
    }
    entry.bookings.push(b);
  }

  const rows = Array.from(map.values()).sort((a: UserBookings, b: UserBookings) =>
    a.userName.localeCompare(b.userName),
  );
  for (const row of rows) {
    row.bookings.sort((a: ManagedExcursionBooking, b: ManagedExcursionBooking) =>
      (a.date ?? "").localeCompare(b.date ?? ""),
    );
  }
  return rows;
}

type ExcursionBookingMobileCardProps = {
  booking: ManagedExcursionBooking;
  cachedExcursion?: Excursion;
  hasBulkActionBar: boolean;
  /** Delete row/column: `leave.validate` and not `CustomersSync` (synced tenants). */
  bookingDeleteAllowed: boolean;
  bulkPending: boolean;
  selectedBookingIds: ReadonlySet<number>;
  toggleBookingSelected: (bookingId: number) => void;
  canActOnBooking: (booking: ManagedExcursionBooking) => boolean;
  handleStatusChange: (bookingID: number, nextStatus: "validated" | "rejected") => void;
  handleDeleteBooking: (bookingId: number) => void;
  onOpenExcursionDrawer?: () => void;
};

function ExcursionBookingMobileCard({
  booking: b,
  cachedExcursion,
  hasBulkActionBar,
  bookingDeleteAllowed,
  bulkPending,
  selectedBookingIds,
  toggleBookingSelected,
  canActOnBooking: canAct,
  handleStatusChange,
  handleDeleteBooking,
  onOpenExcursionDrawer,
}: ExcursionBookingMobileCardProps): React.ReactElement {
  const sc = STATUS_CONFIG[b.status];
  const tenantLabel = formatTenant(b);
  const actionable = canAct(b);

  const card = (
    <Card
      sx={{
        ...hrMobileRequestCardSx,
        mb: 0,
        minWidth: 0,
        maxWidth: "100%",
        overflow: "visible",
        touchAction: "pan-y",
        p: { xs: 0.85, sm: 1.5 },
        ...(cachedExcursion ? { cursor: "pointer" as const } : {}),
      }}
      onClick={
        cachedExcursion && onOpenExcursionDrawer
          ? (ev: React.MouseEvent) => {
              const el = ev.target as HTMLElement;
              if (
                el.closest("button") ||
                el.closest('input[type="checkbox"]') ||
                el.closest('[role="checkbox"]') ||
                el.closest("[data-excursion-booking-status-cell]")
              ) {
                return;
              }

              onOpenExcursionDrawer();
            }
          : undefined
      }
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: { xs: 0.65, sm: 1 },
          mb: { xs: 0.65, sm: 1 },
          flexWrap: "nowrap",
        }}
      >
        {hasBulkActionBar ? (
          <Box sx={{ flexShrink: 0 }} onClick={(ev) => ev.stopPropagation()}>
            {actionable ? (
              <DarkCheckbox
                checked={selectedBookingIds.has(b.id)}
                onChange={() => toggleBookingSelected(b.id)}
                disabled={bulkPending}
                slotProps={{
                  root: {
                    onClick: (ev: React.MouseEvent) => {
                      ev.stopPropagation();
                    },
                  },
                }}
                aria-label="Sélectionner cette prestation pour une action en masse"
              />
            ) : (
              <Box sx={{ width: "var(--Checkbox-size, 18px)" }} aria-hidden />
            )}
          </Box>
        ) : null}
        <Box sx={{ flex: "1 1 auto", minWidth: 0 }}>
          <DarkTypography
            level="body-xs"
            sx={{ fontWeight: "bold", fontSize: { xs: "0.8rem", sm: undefined } }}
          >
            {formatDate(b.date)}
          </DarkTypography>
        </Box>
        <Box
          data-excursion-booking-status-cell
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            flexWrap: "nowrap",
            flexShrink: 0,
            justifyContent: "flex-end",
            minWidth: 0,
          }}
        >
          <Chip
            variant="soft"
            color={sc.color}
            size="sm"
            sx={{ flexShrink: 1, minWidth: 0, maxWidth: "100%", px: 0.5 }}
            title={sc.label}
          >
            <Box
              component="span"
              sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
            >
              {sc.label}
            </Box>
          </Chip>
          {actionable ? (
            <Box
              sx={{
                display: "flex",
                flexWrap: "nowrap",
                alignItems: "center",
                gap: 0.5,
                flexShrink: 0,
              }}
            >
              <IconButtonCTA
                size="sm"
                ctaVariant="success"
                aria-label="Valider la prestation"
                disabled={bulkPending}
                onClick={(ev) => {
                  ev.stopPropagation();
                  void handleStatusChange(b.id, "validated");
                }}
                sx={excursionBookingCompactCtaSx}
              >
                <CheckRoundedIcon sx={{ fontSize: 14 }} />
              </IconButtonCTA>
              <IconButtonCTA
                size="sm"
                ctaVariant="danger"
                aria-label="Refuser la prestation"
                disabled={bulkPending}
                onClick={(ev) => {
                  ev.stopPropagation();
                  void handleStatusChange(b.id, "rejected");
                }}
                sx={excursionBookingCompactCtaSx}
              >
                <CloseRoundedIcon sx={{ fontSize: 14 }} />
              </IconButtonCTA>
            </Box>
          ) : null}
        </Box>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: { xs: 0.5, sm: 0.85 },
          columnGap: { xs: 0.85, sm: 1.15 },
          mb: bookingDeleteAllowed && b.status !== "validated" ? { xs: 0.5, sm: 0.85 } : 0,
          "& > *": { minWidth: 0 },
        }}
      >
        <Box>
          <DarkTypography
            level="body-xs"
            sx={{ color: textTokens.color.faint, lineHeight: 1.15, mb: 0.2 }}
          >
            Société
          </DarkTypography>
          <DarkTypography level="body-xs" sx={{ wordBreak: "break-word", lineHeight: 1.25 }}>
            {tenantLabel}
          </DarkTypography>
        </Box>
        <Box>
          <DarkTypography
            level="body-xs"
            sx={{ color: textTokens.color.faint, lineHeight: 1.15, mb: 0.2 }}
          >
            Horaire
          </DarkTypography>
          <DarkTypography level="body-xs" sx={{ wordBreak: "break-word", lineHeight: 1.25 }}>
            {formatTime(b.start_time, b.end_time) || "—"}
          </DarkTypography>
        </Box>
        <Box>
          <DarkTypography
            level="body-xs"
            sx={{ color: textTokens.color.faint, lineHeight: 1.15, mb: 0.2 }}
          >
            Type de sortie
          </DarkTypography>
          <DarkTypography level="body-xs" sx={{ wordBreak: "break-word", lineHeight: 1.25 }}>
            {b.excursion_template || "—"}
          </DarkTypography>
        </Box>
        <Box>
          <DarkTypography
            level="body-xs"
            sx={{ color: textTokens.color.faint, lineHeight: 1.15, mb: 0.2 }}
          >
            Type de groupe
          </DarkTypography>
          <DarkTypography level="body-xs" sx={{ wordBreak: "break-word", lineHeight: 1.25 }}>
            {b.group_template || "—"}
          </DarkTypography>
        </Box>
      </Box>

      {bookingDeleteAllowed && b.status !== "validated" && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", pt: { xs: 0.2, sm: 0.35 } }}>
          <IconButtonCTA
            size="sm"
            ctaVariant="danger"
            disabled={bulkPending}
            onClick={(ev) => {
              ev.stopPropagation();
              void handleDeleteBooking(b.id);
            }}
            aria-label="Supprimer la prestation"
            sx={excursionBookingMobileDeleteCtaSx}
          >
            <DeleteForeverIcon sx={{ fontSize: 18 }} />
          </IconButtonCTA>
        </Box>
      )}
    </Card>
  );

  return card;
}

function bookingTableRowInteractionBlocksDrawer(target: HTMLElement): boolean {
  return Boolean(
    target.closest("button") ||
    target.closest('input[type="checkbox"]') ||
    target.closest('[role="checkbox"]') ||
    target.closest("[data-excursion-booking-status-cell]"),
  );
}

export default function ExcursionBookingPage() {
  const {
    currentMonth,
    bookings,
    loading,
    setYearMonth,
    reload,
    deleteExcursionBooking,
    updateExcursionBookingStatus,
  } = useExcursionBookingsForMonth();
  /** Anonymized cross-tenant rows belong on the grid, not here. */
  const bookingsForManagement = React.useMemo((): ManagedExcursionBooking[] => {
    return bookings.filter(isExcursionBookingForManagementUI);
  }, [bookings]);
  const { getExcursionsBatch, bulkUpdateExcursionBookingStatus } = useExcursion();
  const { getUserName, user } = useAuth();
  const { hasPermission } = usePermissions();
  const { showConfirmation } = useConfirmationModal();
  const { config } = useAppSettings();
  const hasLeaveValidate = hasPermission(Permission.LEAVE_VALIDATE);
  const bookingDeleteAllowed = hasLeaveValidate && config?.CustomersSync !== true;
  const currentUserID = user?.id;
  const [selectedInstructorId, setSelectedInstructorId] = React.useState(INSTRUCTOR_FILTER_ALL);
  const [selectedStatusFilter, setSelectedStatusFilter] =
    React.useState<BookingStatusFilterValue>(BOOKING_STATUS_FILTER_ALL);
  const [selectedBookingIds, setSelectedBookingIds] = React.useState(() => new Set<number>());
  const [bulkPending, setBulkPending] = React.useState(false);
  const [drawerExcursionId, setDrawerExcursionId] = React.useState<number | null>(null);
  const [excursionCache, setExcursionCache] = React.useState<Record<number, Excursion>>({});

  React.useEffect(() => {
    const ids = Array.from(new Set(bookingsForManagement.map((b) => b.excursion_id)));
    if (ids.length === 0) return;
    let cancelled = false;
    getExcursionsBatch(ids)
      .then((list) => {
        if (cancelled || !list) return;
        const map: Record<number, Excursion> = {};
        for (const ex of list) map[ex.id] = ex;
        setExcursionCache(map);
      })
      .catch(() => {
        /* silent */
      });
    return () => {
      cancelled = true;
    };
  }, [bookingsForManagement, getExcursionsBatch]);

  const bookingUserNameMap = React.useMemo(() => {
    const map: Record<number, string> = {};
    for (const b of bookingsForManagement) {
      const name = b.user_name?.trim();
      if (name) map[b.user_id] = name;
    }

    return map;
  }, [bookingsForManagement]);

  const bookingStatusMapByExcursion = React.useMemo(() => {
    const map: Record<number, Record<number, BookingStatus>> = {};
    for (const b of bookingsForManagement) {
      if (!map[b.excursion_id]) map[b.excursion_id] = {};
      map[b.excursion_id][b.user_id] = b.status;
    }

    return map;
  }, [bookingsForManagement]);

  const cachedExcursions = React.useMemo(() => Object.values(excursionCache), [excursionCache]);
  const { getGroupLabels, getCustomerInProgressTrainingsTooltip } = useExcursionLabels(
    cachedExcursions,
    bookingUserNameMap,
  );

  const drawerExcursion =
    drawerExcursionId != null ? (excursionCache[drawerExcursionId] ?? null) : null;

  const closeExcursionDrawer = React.useCallback(() => {
    setDrawerExcursionId(null);
  }, []);

  const openExcursionDrawer = React.useCallback(
    (excursionId: number) => {
      if (!excursionCache[excursionId]) return;

      setDrawerExcursionId(excursionId);
    },
    [excursionCache],
  );

  const instructorOptions = React.useMemo(() => {
    const byId = new Map<number, Pick<InstructorStat, "id" | "first_name" | "last_name">>();
    for (const b of bookingsForManagement) {
      if (!byId.has(b.user_id)) {
        byId.set(b.user_id, instructorOptionFromExcursionBooking(b, getUserName));
      }
    }

    return sortBookingInstructorOptions(Array.from(byId.values()));
  }, [bookingsForManagement, getUserName]);

  const showInstructorFilter = instructorOptions.length > 1;

  React.useEffect(() => {
    if (instructorOptions.length <= 1) {
      if (selectedInstructorId !== INSTRUCTOR_FILTER_ALL) {
        setSelectedInstructorId(INSTRUCTOR_FILTER_ALL);
      }

      return;
    }

    const ids = new Set(instructorOptions.map((o) => o.id));
    if (selectedInstructorId !== INSTRUCTOR_FILTER_ALL && !ids.has(selectedInstructorId)) {
      setSelectedInstructorId(INSTRUCTOR_FILTER_ALL);
    }
  }, [instructorOptions, selectedInstructorId]);

  const handleDeleteBooking = React.useCallback(
    async (bookingId: number) => {
      if (!bookingDeleteAllowed) return;
      const ok = await showConfirmation(
        "Supprimer la prestation",
        "Supprimer cette prestation de la liste ?",
      );
      if (!ok) return;
      await deleteExcursionBooking(bookingId);
      reload();
    },
    [bookingDeleteAllowed, showConfirmation, deleteExcursionBooking, reload],
  );

  const handleStatusChange = React.useCallback(
    async (bookingID: number, nextStatus: "validated" | "rejected") => {
      const title = nextStatus === "validated" ? "Valider la prestation" : "Refuser la prestation";
      const message =
        nextStatus === "validated"
          ? "Confirmer la validation de cette prestation ?"
          : "Confirmer le refus de cette prestation ?";
      const ok = await showConfirmation(title, message);
      if (!ok) return;
      await updateExcursionBookingStatus(bookingID, nextStatus);
      reload();
    },
    [showConfirmation, updateExcursionBookingStatus, reload],
  );

  const canActOnBooking = React.useCallback(
    (booking: ManagedExcursionBooking): boolean => {
      if (booking.status !== "pending") return false;
      const isOwnBooking = currentUserID != null && booking.user_id === currentUserID;
      if (isOwnBooking) return true;
      return hasLeaveValidate && booking.user_is_employee === true;
    },
    [currentUserID, hasLeaveValidate],
  );

  const filteredBookings = React.useMemo(() => {
    let list = bookingsForManagement;
    if (selectedInstructorId !== INSTRUCTOR_FILTER_ALL) {
      list = list.filter((b) => b.user_id === selectedInstructorId);
    }
    if (selectedStatusFilter !== BOOKING_STATUS_FILTER_ALL) {
      list = list.filter((b) => b.status === selectedStatusFilter);
    }

    return list;
  }, [bookingsForManagement, selectedInstructorId, selectedStatusFilter]);

  const selectableBookings = React.useMemo(
    () => filteredBookings.filter((b) => canActOnBooking(b)),
    [filteredBookings, canActOnBooking],
  );

  const selectableBookingIdSet = React.useMemo(
    () => new Set(selectableBookings.map((b) => b.id)),
    [selectableBookings],
  );

  const hasBulkActionBar = selectableBookings.length > 0;

  React.useEffect(() => {
    setSelectedBookingIds((prev) => {
      let pruned = false;
      const next = new Set<number>();
      for (const id of prev) {
        if (selectableBookingIdSet.has(id)) {
          next.add(id);
        } else {
          pruned = true;
        }
      }
      if (!pruned && next.size === prev.size) {
        return prev;
      }

      return next;
    });
  }, [selectableBookingIdSet]);

  const selectableBookingIdsSorted = React.useMemo(
    () => selectableBookings.map((b) => b.id),
    [selectableBookings],
  );

  const selectedBulkCount = React.useMemo(() => {
    let n = 0;
    for (const id of selectedBookingIds) {
      if (selectableBookingIdSet.has(id)) n++;
    }

    return n;
  }, [selectedBookingIds, selectableBookingIdSet]);

  const allSelectableChosen =
    selectableBookingIdsSorted.length > 0 &&
    selectableBookingIdsSorted.every((id) => selectedBookingIds.has(id));

  const someSelectableChosen =
    !allSelectableChosen && selectableBookingIdsSorted.some((id) => selectedBookingIds.has(id));

  const toggleBookingSelected = React.useCallback((bookingId: number) => {
    setSelectedBookingIds((prev) => {
      const next = new Set(prev);
      if (next.has(bookingId)) {
        next.delete(bookingId);
      } else {
        next.add(bookingId);
      }

      return next;
    });
  }, []);

  const toggleSelectAllSelectable = React.useCallback(() => {
    setSelectedBookingIds((prev) => {
      if (selectableBookingIdsSorted.length === 0) {
        return prev;
      }

      const allIn = selectableBookingIdsSorted.every((id) => prev.has(id));
      if (allIn) {
        return new Set();
      }

      return new Set(selectableBookingIdsSorted);
    });
  }, [selectableBookingIdsSorted]);

  const handleBulkStatusChange = React.useCallback(
    async (nextStatus: "validated" | "rejected") => {
      const ids = selectableBookingIdsSorted.filter((id) => selectedBookingIds.has(id));
      if (ids.length === 0) return;

      const title = nextStatus === "validated" ? "Valider en masse" : "Refuser en masse";
      const message =
        nextStatus === "validated"
          ? `Confirmer la validation de ${ids.length} prestation${ids.length > 1 ? "s" : ""} ?`
          : `Confirmer le refus de ${ids.length} prestation${ids.length > 1 ? "s" : ""} ?`;
      const ok = await showConfirmation(title, message);
      if (!ok) return;

      setBulkPending(true);
      try {
        const { succeeded } = await bulkUpdateExcursionBookingStatus(ids, nextStatus);
        if (succeeded > 0) {
          setSelectedBookingIds(new Set());
          reload();
        }
      } finally {
        setBulkPending(false);
      }
    },
    [
      selectableBookingIdsSorted,
      selectedBookingIds,
      showConfirmation,
      bulkUpdateExcursionBookingStatus,
      reload,
    ],
  );

  const userRows = React.useMemo(
    () => groupByUser(filteredBookings, getUserName),
    [filteredBookings, getUserName],
  );

  const currentYear = React.useMemo(() => new Date().getFullYear(), []);
  const years = React.useMemo(
    () => Array.from({ length: Math.max(1, currentYear - 2025 + 1) }, (_, i) => currentYear - i),
    [currentYear],
  );
  const selectedYear = currentMonth.getFullYear();
  const selectedMonth = currentMonth.getMonth() + 1;

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        boxSizing: "border-box",
        p: { xs: 1, sm: 2 },
        pb: { xs: 1, sm: 2 },
        gap: 2,
      }}
    >
      <Box sx={{ flexShrink: 0, minWidth: 0 }}>
        <YearMonthPeriodSelector
          years={years}
          selectedYear={selectedYear}
          monthAndFiltersInline
          onYearChange={(y) => {
            setSelectedInstructorId(INSTRUCTOR_FILTER_ALL);
            setSelectedStatusFilter(BOOKING_STATUS_FILTER_ALL);
            setYearMonth(y, selectedMonth);
          }}
          selectedMonth={selectedMonth}
          onMonthChange={(m) => setYearMonth(selectedYear, m)}
          afterMonthSlot={
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                flexWrap: "nowrap",
                gap: { xs: 0.75, sm: 2 },
                flex: "1 1 0%",
                minWidth: 0,
                alignItems: "flex-end",
              }}
            >
              {showInstructorFilter && (
                <InstructorEncadrantFilter
                  value={selectedInstructorId}
                  onInstructorChange={setSelectedInstructorId}
                  options={instructorOptions}
                  sx={{
                    flex: { xs: "1 1 0%", sm: "1 1 220px" },
                    maxWidth: { xs: "none", sm: 360 },
                  }}
                />
              )}
              <Box
                sx={{
                  flex: showInstructorFilter ? { xs: "1 1 0%", sm: "0 1 200px" } : "1 1 auto",
                  maxWidth: showInstructorFilter
                    ? { xs: "none", sm: 240 }
                    : { xs: "95vw", sm: 280 },
                  minWidth: 0,
                }}
              >
                <DarkTypography
                  level="body-sm"
                  colorVariant="primary"
                  sx={{ mb: { xs: 0.25, sm: 0.5 } }}
                >
                  Statut
                </DarkTypography>
                <DarkSelect
                  size="sm"
                  value={selectedStatusFilter}
                  onChange={(_, v) =>
                    setSelectedStatusFilter(
                      (v ?? BOOKING_STATUS_FILTER_ALL) as BookingStatusFilterValue,
                    )
                  }
                  aria-label="Statut de la prestation"
                  sx={{ width: "100%" }}
                >
                  <Option value={BOOKING_STATUS_FILTER_ALL}>Tous les statuts</Option>
                  {BOOKING_STATUS_OPTIONS.map((st) => (
                    <Option key={st} value={st}>
                      {STATUS_CONFIG[st].label}
                    </Option>
                  ))}
                </DarkSelect>
              </Box>
            </Box>
          }
          endSlot={loading ? <CircularProgress size="sm" /> : undefined}
        />
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          minWidth: 0,
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: "stretch",
        }}
      >
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            minWidth: 0,
            overflowY: "auto",
            overflowX: "hidden",
            WebkitOverflowScrolling: "touch",
            touchAction: "pan-y",
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          {bookingsForManagement.length === 0 && !loading && (
            <DarkTypography level="body-md" sx={{ color: textTokens.color.dimmed, mt: 2 }}>
              Aucune prestation pour cette période.
            </DarkTypography>
          )}

          {bookingsForManagement.length > 0 && filteredBookings.length === 0 && !loading && (
            <DarkTypography level="body-md" sx={{ color: textTokens.color.dimmed, mt: 2 }}>
              Aucune prestation ne correspond aux filtres sélectionnés.
            </DarkTypography>
          )}

          {hasBulkActionBar &&
            bookingsForManagement.length > 0 &&
            filteredBookings.length > 0 &&
            !loading && (
              <DarkCard dense sx={{ minWidth: 0, maxWidth: "100%", flexShrink: 0 }}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    flexWrap: "wrap",
                    alignItems: "center",
                    gap: { xs: 0.75, sm: 1.5 },
                  }}
                >
                  <DarkCheckbox
                    size="sm"
                    checked={allSelectableChosen}
                    indeterminate={someSelectableChosen}
                    onChange={() => toggleSelectAllSelectable()}
                    disabled={bulkPending}
                    label={`Tout (${selectableBookingIdsSorted.length})`}
                    aria-label={`Tout sélectionner ${selectableBookingIdsSorted.length} prestation${selectableBookingIdsSorted.length > 1 ? "s" : ""}`}
                    sx={{
                      "--Checkbox-size": "16px",
                      "--Icon-fontSize": "12px",
                      alignItems: "center",
                      "& .MuiCheckbox-label": {
                        fontSize: "0.78rem",
                        lineHeight: 1.25,
                      },
                    }}
                  />
                  {selectedBulkCount > 0 ? (
                    <DarkTypography
                      level="body-sm"
                      sx={{ color: textTokens.color.dimmed, minWidth: 0 }}
                    >
                      {`${selectedBulkCount} sélectionnée${selectedBulkCount > 1 ? "s" : ""}`}
                    </DarkTypography>
                  ) : null}
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "nowrap",
                      alignItems: "center",
                      gap: 0.75,
                      flexShrink: 0,
                      ml: "auto",
                    }}
                  >
                    <IconButtonCTA
                      size="sm"
                      ctaVariant="success"
                      aria-label="Valider les prestations sélectionnées"
                      disabled={selectedBulkCount === 0 || bulkPending}
                      onClick={() => void handleBulkStatusChange("validated")}
                      sx={excursionBookingCompactCtaSx}
                    >
                      <CheckRoundedIcon sx={{ fontSize: 14 }} />
                    </IconButtonCTA>
                    <IconButtonCTA
                      size="sm"
                      ctaVariant="danger"
                      aria-label="Refuser les prestations sélectionnées"
                      disabled={selectedBulkCount === 0 || bulkPending}
                      onClick={() => void handleBulkStatusChange("rejected")}
                      sx={excursionBookingCompactCtaSx}
                    >
                      <CloseRoundedIcon sx={{ fontSize: 14 }} />
                    </IconButtonCTA>
                    {bulkPending ? <CircularProgress size="sm" /> : null}
                  </Box>
                </Box>
              </DarkCard>
            )}

          {userRows.map((row) => (
            <DarkCard
              key={row.userId}
              dense
              sx={{ minWidth: 0, width: "100%", maxWidth: "100%", flexShrink: 0 }}
            >
              <DarkTypography
                level="title-md"
                colorVariant="accent"
                sx={{
                  mb: { xs: 0.5, sm: 1 },
                  wordBreak: "break-word",
                  fontSize: { xs: "1rem", sm: undefined },
                }}
              >
                {row.userName}
                <DarkTypography
                  component="span"
                  level="body-sm"
                  sx={{ color: textTokens.color.faint, ml: 1 }}
                >
                  ({row.bookings.length} prestation{row.bookings.length > 1 ? "s" : ""})
                </DarkTypography>
              </DarkTypography>
              <Sheet
                sx={{
                  ...hrSheetSx,
                  borderRadius: "6px",
                  overflowX: "auto",
                  display: { xs: "none", md: "block" },
                  maxWidth: "100%",
                }}
              >
                <Table
                  hoverRow
                  sx={{
                    ...hrTableSx,
                    minWidth: (hasBulkActionBar ? 72 : 0) + 560 + (bookingDeleteAllowed ? 56 : 0),
                  }}
                >
                  <thead>
                    <tr>
                      {hasBulkActionBar ? (
                        <th style={{ width: 44 }} aria-label="Sélection pour validation en masse" />
                      ) : null}
                      <th>Date</th>
                      <th>Société</th>
                      <th>Horaire</th>
                      <th>Type de sortie</th>
                      <th>Type de groupe</th>
                      <th>Statut</th>
                      {bookingDeleteAllowed && <th style={{ width: 56 }} aria-hidden />}
                    </tr>
                  </thead>
                  <tbody>
                    {row.bookings.map((b) => {
                      const sc = STATUS_CONFIG[b.status];
                      const tenantLabel = formatTenant(b);
                      const cachedEx = excursionCache[b.excursion_id];
                      const rowEl = (
                        <tr
                          key={b.id}
                          style={cachedEx ? { cursor: "pointer" } : undefined}
                          onClick={
                            cachedEx
                              ? (ev: React.MouseEvent<HTMLTableRowElement>) => {
                                  if (
                                    bookingTableRowInteractionBlocksDrawer(ev.target as HTMLElement)
                                  )
                                    return;

                                  openExcursionDrawer(b.excursion_id);
                                }
                              : undefined
                          }
                        >
                          {hasBulkActionBar ? (
                            <td>
                              {canActOnBooking(b) ? (
                                <DarkCheckbox
                                  checked={selectedBookingIds.has(b.id)}
                                  onChange={() => toggleBookingSelected(b.id)}
                                  disabled={bulkPending}
                                  slotProps={{
                                    root: {
                                      onClick: (ev: React.MouseEvent) => {
                                        ev.stopPropagation();
                                      },
                                    },
                                  }}
                                  aria-label="Sélectionner cette prestation pour une action en masse"
                                />
                              ) : (
                                <Box sx={{ width: "var(--Checkbox-size, 18px)" }} aria-hidden />
                              )}
                            </td>
                          ) : null}
                          <td>
                            <DarkTypography level="body-sm" sx={{ fontWeight: "bold" }}>
                              {formatDate(b.date)}
                            </DarkTypography>
                          </td>
                          <td>
                            <DarkTypography
                              level="body-sm"
                              sx={{
                                minWidth: 0,
                                maxWidth: { xs: "min(40vw, 200px)", sm: 240 },
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                              title={tenantLabel !== "—" ? tenantLabel : undefined}
                            >
                              {tenantLabel}
                            </DarkTypography>
                          </td>
                          <td>
                            <DarkTypography level="body-sm">
                              {formatTime(b.start_time, b.end_time) || "—"}
                            </DarkTypography>
                          </td>
                          <td>
                            <DarkTypography level="body-sm">
                              {b.excursion_template || "—"}
                            </DarkTypography>
                          </td>
                          <td>
                            <DarkTypography level="body-sm">
                              {b.group_template || "—"}
                            </DarkTypography>
                          </td>
                          <td data-excursion-booking-status-cell>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                                flexWrap: "nowrap",
                                minWidth: 0,
                              }}
                            >
                              <Chip
                                variant="soft"
                                color={sc.color}
                                size="sm"
                                sx={{ flexShrink: 1, minWidth: 0 }}
                                title={sc.label}
                              >
                                <Box
                                  component="span"
                                  sx={{
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {sc.label}
                                </Box>
                              </Chip>
                              {canActOnBooking(b) ? (
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexWrap: "nowrap",
                                    alignItems: "center",
                                    gap: 0.5,
                                    flexShrink: 0,
                                  }}
                                >
                                  <IconButtonCTA
                                    size="sm"
                                    ctaVariant="success"
                                    aria-label="Valider la prestation"
                                    disabled={bulkPending}
                                    onClick={() => handleStatusChange(b.id, "validated")}
                                    sx={excursionBookingCompactCtaSx}
                                  >
                                    <CheckRoundedIcon sx={{ fontSize: 14 }} />
                                  </IconButtonCTA>
                                  <IconButtonCTA
                                    size="sm"
                                    ctaVariant="danger"
                                    aria-label="Refuser la prestation"
                                    disabled={bulkPending}
                                    onClick={() => handleStatusChange(b.id, "rejected")}
                                    sx={excursionBookingCompactCtaSx}
                                  >
                                    <CloseRoundedIcon sx={{ fontSize: 14 }} />
                                  </IconButtonCTA>
                                </Box>
                              ) : null}
                            </Box>
                          </td>
                          {bookingDeleteAllowed && (
                            <td>
                              {b.status !== "validated" && (
                                <IconButtonCTA
                                  size="sm"
                                  ctaVariant="danger"
                                  disabled={bulkPending}
                                  onClick={() => handleDeleteBooking(b.id)}
                                  aria-label="Supprimer la prestation"
                                  sx={excursionBookingCompactCtaSx}
                                >
                                  <DeleteForeverIcon sx={{ fontSize: 18 }} />
                                </IconButtonCTA>
                              )}
                            </td>
                          )}
                        </tr>
                      );
                      return rowEl;
                    })}
                  </tbody>
                </Table>
              </Sheet>

              <Box
                sx={{
                  display: { xs: "flex", md: "none" },
                  flexDirection: "column",
                  gap: { xs: 0.5, sm: 1 },
                }}
              >
                {row.bookings.map((b) => (
                  <ExcursionBookingMobileCard
                    key={b.id}
                    booking={b}
                    cachedExcursion={excursionCache[b.excursion_id]}
                    hasBulkActionBar={hasBulkActionBar}
                    bookingDeleteAllowed={bookingDeleteAllowed}
                    bulkPending={bulkPending}
                    selectedBookingIds={selectedBookingIds}
                    toggleBookingSelected={toggleBookingSelected}
                    canActOnBooking={canActOnBooking}
                    handleStatusChange={handleStatusChange}
                    handleDeleteBooking={handleDeleteBooking}
                    onOpenExcursionDrawer={
                      excursionCache[b.excursion_id]
                        ? () => openExcursionDrawer(b.excursion_id)
                        : undefined
                    }
                  />
                ))}
              </Box>
            </DarkCard>
          ))}
        </Box>

        <ExcursionPlanningGroupsDrawer
          open={Boolean(drawerExcursion)}
          excursion={drawerExcursion}
          onClose={closeExcursionDrawer}
          canEdit={false}
          showExcursionEditButton={false}
          getGroupLabels={getGroupLabels}
          bookingStatusMap={
            drawerExcursion ? bookingStatusMapByExcursion[drawerExcursion.id] : undefined
          }
          getCustomerInProgressTrainingsTooltip={getCustomerInProgressTrainingsTooltip}
        />
      </Box>
    </Box>
  );
}
